/**
 * 🔄 ELSA GESTION - Fetch avec retry et timeout
 * Optimisé pour réseaux 3G instables (Afrique de l'Ouest)
 *
 * Fonctionnalités :
 * - Timeout configurable selon type de réseau
 * - Retry automatique avec backoff exponentiel
 * - Annulation de requêtes (AbortController)
 * - Gestion intelligente des erreurs
 * - Notifications utilisateur optionnelles
 */

import { message } from 'antd';
import {
  beginGlobalLoading,
  shouldTrackGlobalLoading,
} from './globalLoading.js';

/**
 * Configuration par défaut
 */
const DEFAULT_CONFIG = {
  timeout: 10000, // 10 secondes
  retries: 3,
  retryDelay: 1000, // 1 seconde
  backoffMultiplier: 2, // Délai x2 à chaque retry
  showNotifications: false,
  notifyOnRetry: false,
  notifyOnError: true,
};

/**
 * Erreur avec métadonnées supplémentaires
 */
class FetchError extends Error {
  constructor(message, statusCode = null, response = null) {
    super(message);
    this.name = 'FetchError';
    this.statusCode = statusCode;
    this.response = response;
    this.isTimeout = message.includes('timeout') || message.includes('Timeout');
    this.isNetworkError = message.includes('network') || message.includes('Failed to fetch');
  }
}

/**
 * Fetch avec timeout
 * @param {string} url - URL de la requête
 * @param {Object} options - Options fetch standard
 * @param {number} timeout - Timeout en ms
 * @returns {Promise<Response>}
 */
async function fetchWithTimeout(url, options = {}, timeout = 10000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new FetchError(`Requête timeout après ${timeout}ms`, null, null);
    }

    throw error;
  }
}

/**
 * Délai asynchrone
 * @param {number} ms - Millisecondes
 * @returns {Promise<void>}
 */
function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Vérifier si une erreur est récupérable (peut retry)
 * @param {Error} error - Erreur à vérifier
 * @param {Response} response - Réponse HTTP (si disponible)
 * @returns {boolean}
 */
function isRetryableError(error, response = null) {
  // Erreurs réseau (pas de réponse) → retry
  if (!response) {
    return true;
  }

  // Codes HTTP retryables
  const retryableStatusCodes = [
    408, // Request Timeout
    429, // Too Many Requests
    500, // Internal Server Error
    502, // Bad Gateway
    503, // Service Unavailable
    504, // Gateway Timeout
  ];

  return retryableStatusCodes.includes(response.status);
}

/**
 * Fetch avec retry automatique et timeout adaptatif
 *
 * @param {string} url - URL de la requête
 * @param {Object} options - Options de configuration
 * @param {Object} options.fetchOptions - Options fetch standard (method, headers, body, etc.)
 * @param {number} options.timeout - Timeout en ms (défaut: 10000)
 * @param {number} options.retries - Nombre de tentatives (défaut: 3)
 * @param {number} options.retryDelay - Délai initial entre retries en ms (défaut: 1000)
 * @param {number} options.backoffMultiplier - Multiplicateur backoff exponentiel (défaut: 2)
 * @param {boolean} options.showNotifications - Afficher notifications Ant Design (défaut: false)
 * @param {boolean} options.notifyOnRetry - Notifier lors des retries (défaut: false)
 * @param {boolean} options.notifyOnError - Notifier les erreurs finales (défaut: true)
 * @param {Function} options.onRetry - Callback lors d'un retry (attempt, error)
 * @param {Function} options.onSuccess - Callback en cas de succès (response, attempt)
 * @param {Function} options.onError - Callback en cas d'échec final (error, attempts)
 *
 * @returns {Promise<Response>}
 *
 * @example
 * // Utilisation simple
 * const response = await fetchWithRetry('/api/products', {
 *   timeout: 15000,
 *   retries: 3,
 * });
 *
 * @example
 * // Avec callbacks
 * const response = await fetchWithRetry('/api/products', {
 *   timeout: 10000,
 *   retries: 5,
 *   showNotifications: true,
 *   onRetry: (attempt, error) => {
 *     console.log(`Tentative ${attempt} échouée:`, error.message);
 *   },
 *   onSuccess: (response, attempt) => {
 *     console.log(`Succès après ${attempt} tentative(s)`);
 *   },
 * });
 */
export async function fetchWithRetry(url, options = {}) {
  const finishGlobalLoading = shouldTrackGlobalLoading(options)
    ? beginGlobalLoading()
    : null;

  try {
    return await executeFetchWithRetry(url, options);
  } finally {
    finishGlobalLoading?.();
  }
}

async function executeFetchWithRetry(url, options = {}) {
  const config = {
    ...DEFAULT_CONFIG,
    ...options,
  };

  const {
    fetchOptions = {},
    timeout,
    retries,
    retryDelay,
    backoffMultiplier,
    showNotifications,
    notifyOnRetry,
    notifyOnError,
    onRetry,
    onSuccess,
    onError,
  } = config;

  let lastError = null;
  let currentDelay = retryDelay;

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      console.log(`[Fetch] Tentative ${attempt}/${retries}: ${url}`);

      const response = await fetchWithTimeout(
        url,
        { ...fetchOptions, skipGlobalLoading: true },
        timeout
      );

      // Vérifier le code de statut
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Erreur inconnue');

        const error = new FetchError(
          `Erreur HTTP ${response.status}: ${errorText}`,
          response.status,
          response
        );

        // Vérifier si on peut retry
        if (!isRetryableError(error, response)) {
          throw error; // Erreur non-retryable, sortir immédiatement
        }

        lastError = error;

        // Si c'est la dernière tentative, lancer l'erreur
        if (attempt === retries) {
          throw error;
        }

        // Callback retry
        if (onRetry) {
          onRetry(attempt, error);
        }

        // Notification retry
        if (showNotifications && notifyOnRetry) {
          message.warning(
            `Connexion instable. Nouvelle tentative ${attempt + 1}/${retries}...`,
            2
          );
        }

        // Attendre avant le prochain retry
        console.log(`[Fetch] Retry dans ${currentDelay}ms (Erreur ${response.status})`);
        await delay(currentDelay);
        currentDelay *= backoffMultiplier;

        continue;
      }

      // ✅ Succès !
      console.log(`[Fetch] ✅ Succès après ${attempt} tentative(s): ${url}`);

      if (onSuccess) {
        onSuccess(response, attempt);
      }

      if (showNotifications && attempt > 1) {
        message.success('Connexion rétablie', 2);
      }

      return response;
    } catch (error) {
      lastError = error;

      // Si c'est la dernière tentative, lancer l'erreur
      if (attempt === retries) {
        break;
      }

      // Callback retry
      if (onRetry) {
        onRetry(attempt, error);
      }

      // Notification retry
      if (showNotifications && notifyOnRetry) {
        message.warning(
          `Connexion instable. Nouvelle tentative ${attempt + 1}/${retries}...`,
          2
        );
      }

      // Attendre avant le prochain retry
      console.log(`[Fetch] Retry dans ${currentDelay}ms (${error.message})`);
      await delay(currentDelay);
      currentDelay *= backoffMultiplier;
    }
  }

  // ❌ Échec après tous les retries
  console.error(`[Fetch] ❌ Échec après ${retries} tentatives: ${url}`, lastError);

  if (onError) {
    onError(lastError, retries);
  }

  if (showNotifications && notifyOnError) {
    const errorMessage = lastError?.isTimeout
      ? 'Connexion trop lente. Vérifiez votre réseau.'
      : lastError?.isNetworkError
      ? 'Impossible de joindre le serveur. Vérifiez votre connexion.'
      : `Erreur : ${lastError?.message || 'Erreur inconnue'}`;

    message.error(errorMessage, 5);
  }

  throw lastError || new FetchError('Échec après toutes les tentatives');
}

/**
 * Fetch JSON avec retry (helper)
 * @param {string} url - URL de la requête
 * @param {Object} options - Options fetchWithRetry
 * @returns {Promise<Object>} - Données JSON parsées
 */
export async function fetchJSONWithRetry(url, options = {}) {
  const response = await fetchWithRetry(url, options);
  return response.json();
}

/**
 * POST JSON avec retry (helper)
 * @param {string} url - URL de la requête
 * @param {Object} data - Données à envoyer (sera stringifié)
 * @param {Object} options - Options fetchWithRetry
 * @returns {Promise<Object>} - Réponse JSON parsée
 */
export async function postJSONWithRetry(url, data, options = {}) {
  const response = await fetchWithRetry(url, {
    ...options,
    fetchOptions: {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(options.fetchOptions?.headers || {}),
      },
      body: JSON.stringify(data),
      ...options.fetchOptions,
    },
  });

  return response.json();
}

/**
 * Wrapper pour adapter timeout selon le réseau
 * @param {Object} networkStatus - Status du hook useNetworkStatus()
 * @param {string} requestType - Type de requête ('api', 'image', 'video')
 * @returns {number} - Timeout adapté en ms
 */
export function getAdaptiveTimeoutFromNetwork(networkStatus, requestType = 'api') {
  const baseTimeouts = {
    api: { fast: 5000, moderate: 10000, slow: 15000 },
    image: { fast: 5000, moderate: 10000, slow: 15000 },
    video: { fast: 10000, moderate: 20000, slow: 30000 },
  };

  const timeouts = baseTimeouts[requestType] || baseTimeouts.api;

  if (networkStatus.isFast) return timeouts.fast;
  if (networkStatus.isModerate) return timeouts.moderate;
  return timeouts.slow;
}

export default {
  fetchWithRetry,
  fetchJSONWithRetry,
  postJSONWithRetry,
  getAdaptiveTimeoutFromNetwork,
  FetchError,
};
